import { motion } from "framer-motion";
import { Link } from "wouter";
import { ArrowRight, Code, Globe, Smartphone, BarChart, ShoppingCart, Briefcase, PenTool, Shield, Cloud, Settings, Mail, Server, Cpu } from "lucide-react";

interface ServiceCardProps {
  title: string;
  description: string;
  icon: string | JSX.Element;
  href: string;
  gradient: string;
}

export default function ServiceCard({ title, description, icon, href, gradient }: ServiceCardProps) {
  return (
    <Link href={href}>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        whileHover={{ y: -8 }}
        transition={{ duration: 0.3 }}
        className="service-card glass-effect p-8 rounded-xl group cursor-pointer"
      >
        <div className={`w-16 h-16 ${gradient ? `bg-gradient-to-r ${gradient}` : 'bg-gradient-to-r from-cyan-400 via-purple-500 to-lime-300'} rounded-lg flex items-center justify-center mb-6 relative overflow-hidden`}>
        {typeof icon === 'string' ? (
          // Handle string icons (legacy support)
          <>
            {icon === 'fas fa-code' && (
              <Code className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-globe' && (
              <Globe className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-mobile-alt' && (
              <Smartphone className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-chart-line' && (
              <BarChart className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-shopping-cart' && (
              <ShoppingCart className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-lightbulb' && (
              <Briefcase className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-paint-brush' && (
              <PenTool className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-search' && (
              <Shield className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-cloud' && (
              <Cloud className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-shield-alt' && (
              <Shield className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-tools' && (
              <Settings className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-mobile' && (
              <Smartphone className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-pen-fancy' && (
              <PenTool className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-envelope' && (
              <Mail className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-server' && (
              <Server className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-globe-americas' && (
              <Globe className="w-12 h-12 relative z-10 text-white" />
            )}
            {icon === 'fas fa-project-diagram' && (
              <Cpu className="w-12 h-12 relative z-10 text-white" />
            )}
            {/* Fallback to icon font if SVG not found */}
            {![
              'fas fa-code', 'fas fa-globe', 'fas fa-mobile-alt', 'fas fa-chart-line',
              'fas fa-shopping-cart', 'fas fa-lightbulb', 'fas fa-paint-brush', 'fas fa-search',
              'fas fa-cloud', 'fas fa-shield-alt', 'fas fa-tools', 'fas fa-mobile',
              'fas fa-pen-fancy', 'fas fa-envelope', 'fas fa-server', 'fas fa-globe-americas',
              'fas fa-project-diagram'
            ].includes(icon) && (
              <i className={`${icon} text-white text-2xl`} />
            )}
          </>
        ) : (
          // Handle JSX.Element icons
          <div className="text-white">
            {icon}
          </div>
        )}
      </div>
      <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
        {title}
      </h3>
      <p className="text-muted-foreground mb-4">{description}</p>
      <div className="flex items-center text-sm text-blue-600 dark:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity">
        <span>Learn more</span>
        <ArrowRight className="ml-1 h-4 w-4" />
      </div>
    </motion.div>
  </Link>
  );
}
